/* eslint-disable react/no-unstable-nested-components */
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
// import cn from "classnames";
import {
    ColumnDef,
    getCoreRowModel,
    Row,
    useReactTable,
} from "@tanstack/react-table";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    Badge,
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
    Tooltip,
} from "@jhool-io/fe-components";
import styles from "./List.module.scss";
import {
    ClientStatus,
    FinancialAssistanceStatus,
    IClient,
    INewClientDetails,
} from "../../../utils/types/client";
import AddClient from "../AddPersonalInfo/AddPersonalInfo";
import { useFetchClientList } from "../../../hooks/queries/client";
import ListState from "../../ListState/ListState";
import { useDebounce } from "../../../hooks/helpers";
import { useAddClient } from "../../../hooks/mutations/client";
import Progress from "../../Progress/Progress";
import { IPaginatedApiResponse } from "../../../utils/types/api-response";
import useToast from "../../../hooks/useToast";
import { UserPermisions, UserRole } from "../../../utils/types/user";
import { useDoesUserHavePermission } from "../../../hooks/permissions";
// import MobileListItem from "../../MobileListItem/MobileListItem";
// import { isMinor } from "../../../utils/helpers/age";
import Navbar from "../../Navbar/Navbar";
import {
    displayNameInRightFormat,
    handleDisplayClientEmailCorrectly,
    isSmallScreen,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
    formatDate,
    appendMidnightTimeToDate,
} from "../../../utils/helpers";
import { getClientAge } from "../../../utils/helpers/age";
import { LISTS_DEFAULT_LIMIT } from "../../../utils/constants";
import useShowInsuranceOverSelfPay from "../../../modules/practice-settings/hooks/useShowInsuranceOverSelfPay";
import { useAppSelector } from "../../../hooks/useRedux";
import AddClientSession from "../../../modules/clients/components/AddClientSession/AddClientSession";
import Loader from "../../../shared-ui/Loader/Loader";
import InsuranceProviderFilter, {
    getInsuranceProviderIdsFilter,
    setInsuranceProviderIdsFilter,
} from "../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";
import {
    useExportClientsList,
    useFetchNormalizedEmployerNames,
} from "../../../modules/clients/hooks/clients.queries";
import CheckPrimaryColorIcon from "../../Icons/CheckPrimaryColor";
import ClientTagsFilter, {
    getClientTagsFilter,
    setClientTagsFilter,
} from "../../../modules/clients/components/ClientTagsFilter/ClientTagsFilter";
import { CLIENT_TAG_DETAILS } from "../../../modules/clients/helpers/clients.helpers";
import { ClientTagKey } from "../../../modules/clients/types/clients.types";

enum FormTypes {
    ADD_PERSONAL_INFO = "add-personal-info",
    ADD_SESSION = "add-session",
}

type SubmitTypes = "save" | "save_continue";

export default function List() {
    const [searchParams, setSearchParams] = useSearchParams();

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    // Local component states
    const [rowSelection, setRowSelection] = useState({});
    const [formInView, setFormInView] = useState<FormTypes | null>(null);
    const [progressStep, setProgressStep] = useState(
        FormTypes.ADD_PERSONAL_INFO
    );
    const [submitType, setSubmitType] = useState<SubmitTypes | null>(null);
    const [addClientFormValues] = useState<INewClientDetails>();
    const [columnVisibilityState, setColumnVisibility] = useState<
        Record<string, boolean>
    >({
        alt_email_sent: true,
    });
    const canViewClientTags = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_TAGS_READ,
    ]);
    const columnVisibility = useMemo(() => {
        const visibility = showInsOverSelfPay
            ? {
                  ...columnVisibilityState,
                  alt_email_sent: false,
              }
            : columnVisibilityState;
        return canViewClientTags ? visibility : { ...visibility, tags: false };
    }, [columnVisibilityState, showInsOverSelfPay, canViewClientTags]);

    const [isExporting, setIsExporting] = useState(false);
    const [employerNameSearchString, setEmployerNameSearchString] =
        useState("");

    const search = searchParams.get("search") || "";
    const insuranceProviderIdsFilter =
        getInsuranceProviderIdsFilter(searchParams);
    const financialAssistanceStatus =
        searchParams.get("financial_assistance_status") || "";

    const patientPortalActivationStatus =
        searchParams.get("patient_portal_activation_status") || "";

    const employerName = searchParams.get("employer_name") || "";

    const clientTagsFilter = getClientTagsFilter(searchParams);

    const { practice } = useAppSelector((state) => state.userPractice);

    const debouncedValue = useDebounce(search, 500);

    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });

    // check if logged in user can edit personal info
    const canAddClient = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_PERSONAL_WRITE,
    ]);

    const canViewInsuranceProvidersList = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_INSURANCE_READ,
        UserPermisions.CLIENT_INFO_INSURANCE_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const handleSetClientStatusFilter = () => {
        if (searchParams.get("status")) {
            if (searchParams.get("status") === "all") {
                return "";
            }
            return searchParams.get("status");
        }
        return ClientStatus.ACTIVE;
    };

    const clientStatusFilter = handleSetClientStatusFilter() || "";

    const handleSetProviderId = () => {
        if (
            !practice?.roles?.includes(UserRole.AUDITOR) &&
            !practice?.roles?.includes(UserRole.SUPER_ADMIN) &&
            !practice?.roles?.includes(UserRole.AUDITOR_ADMIN) &&
            practice?.roles?.includes(UserRole.PROVIDER)
        ) {
            return practice?.provider_id;
        }

        return "";
    };

    const sortableColumns = [
        "client_name",
        "preferred_name",
        "pronouns",
        "date_of_birth",
        "contact",
        "email",
        "employer_name",
    ];

    // Hook for fetching all clients
    const { data, isLoading, isSuccess, error } = useFetchClientList({
        page: pageFilter,
        search_string: debouncedValue.toLowerCase(),
        limit: limitFilter,
        insurance_provider_ids: insuranceProviderIdsFilter,
        provider_id: handleSetProviderId() as string,
        sort_attribute: searchParams.get("sort_attr") || sortConfig.key,
        sort_order: searchParams.get("sort_order") || sortConfig.direction,
        financial_assistance_status: financialAssistanceStatus,
        client_status: clientStatusFilter,
        patient_portal_activation_status: patientPortalActivationStatus,
        employer_name: employerName,
        tag_keys: clientTagsFilter,
    });

    const {
        data: normalizedEmployerNames,
        isLoading: normalizedEmployerNamesLoading,
        error: normalizedEmployerNamesError,
    } = useFetchNormalizedEmployerNames(employerNameSearchString);

    // Query client
    const queryClient = useQueryClient();

    // Hook for toast
    const { toast } = useToast();

    const navigate = useNavigate();

    // Hook for creating new client
    const addClient = useAddClient();

    const handleRowClick = (row: Row<IClient>) => {
        navigate(`/clients/${row.original.client_id}`);
    };

    // const handleMobileItemClick = (clientId: string) => {
    //     navigate(`/clients/${clientId}`);
    // };

    const handleSetFormInView = (form: FormTypes | null) => {
        setFormInView(form);
    };

    // Check if add insurance mutation is currently running, returns 0 || 1
    const addClientSessionMutating = useIsMutating(["add-session"]);

    // Function for handling add client personal detail form submission
    const handleAddClientPersonalDetails = (payload: INewClientDetails) => {
        if (payload) {
            addClient.mutate(payload, {
                onSuccess: async ({ client, message }) => {
                    queryClient.setQueriesData<
                        IPaginatedApiResponse<IClient[]>
                    >(["clients", 1, ""], (prev) => {
                        if (!prev) {
                            return {
                                count: 1,
                                total_count: 0,
                                current_page: 0,
                                total_pages: 0,
                                message: "Clients fetched successfully!",
                                data: [client],
                            };
                        }
                        return {
                            ...prev,
                            count: prev.count + 1,
                            total_count: prev.total_count + 1,
                            message: "Clients fetched successfully!",
                            data: [client, ...prev.data],
                        };
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["clients"],
                    });
                    toast({
                        mode: "success",
                        message: message || "Client created successfully!",
                    });

                    if (submitType === "save_continue") {
                        handleSetFormInView(FormTypes.ADD_SESSION);
                        setProgressStep(FormTypes.ADD_SESSION);
                        searchParams.set("new_client", client.client_id);
                        setSearchParams(searchParams);
                    } else {
                        handleSetFormInView(null);
                        navigate(`/clients/${client.client_id}`);
                    }
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Couldn't add client personal info at this time",
                    });
                },
            });
        }
    };

    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        setInsuranceProviderIdsFilter(searchParams, insuranceProviderIds);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleClientTagsChange = (tagKeys: ClientTagKey[]) => {
        setClientTagsFilter(searchParams, tagKeys);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFinancialAssistanceStatusChange = (fa: string) => {
        if (fa === "") searchParams.delete("financial_assistance_status");
        else searchParams.set("financial_assistance_status", fa);
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handlePatientPortalActivationStatusChange = (status: string) => {
        if (status === "")
            searchParams.delete("patient_portal_activation_status");
        else searchParams.set("patient_portal_activation_status", status);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleEmployerNameChange = (name: string) => {
        if (name === "") searchParams.delete("employer_name");
        else searchParams.set("employer_name", name);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    // Function to call when submitType === "save"
    const handleSubmitAndClose = () => {
        setSubmitType("save");
        handleAddClientPersonalDetails(
            addClientFormValues as INewClientDetails
        );
    };

    // Function to call when submitType === "save_continue"
    const handleSubmitAndContinue = () => {
        setSubmitType("save_continue");
        handleAddClientPersonalDetails(
            addClientFormValues as INewClientDetails
        );
    };

    // Get submit and cancel button "form" value for different submitType
    const addClientPersonalInfoFormValue =
        submitType === "save"
            ? "add-personal-info-save"
            : "add-personal-info-continue";

    // onChange handler for search input
    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);
        searchParams.set("page", "1");

        setSearchParams(searchParams);
    };

    const handleClientStatusChange = (status: string) => {
        searchParams.set("status", status);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    // onChange handler for limit
    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const getPortalStatusDisplayText = () => {
        if (patientPortalActivationStatus === "true") return "Portal Activated";
        if (patientPortalActivationStatus === "false")
            return "Portal Not Activated";
        return "Portal Status";
    };

    // Get client list count
    let clientsCount;
    if (error) clientsCount = "";
    else if (isLoading) clientsCount = "";
    else if (data?.data && data?.data.length === 0) clientsCount = 0;
    else if (data?.data && data?.data.length > 0)
        clientsCount = data.total_count;

    // Cached search suggestions Implementation
    const mantleClientCacheKey = "MANTLE_CACHED_CLIENT_SEARCHES";
    const cachedSearches = localStorage.getItem(mantleClientCacheKey);
    const parsedCachedSearches: string[] = useMemo(() => {
        if (cachedSearches) {
            return JSON.parse(cachedSearches);
        }
        return [];
    }, [cachedSearches]);

    // function handling client search cache implementation
    const storeInCache = useCallback(
        (val: string) => {
            if (cachedSearches) {
                // if val is already in list, move it to the top
                if (parsedCachedSearches.includes(val)) {
                    // delete item
                    parsedCachedSearches.splice(
                        parsedCachedSearches.indexOf(val),
                        1
                    );
                    // add item to the start of the list
                    parsedCachedSearches.unshift(val);
                } else {
                    parsedCachedSearches.unshift(val);
                    parsedCachedSearches.splice(
                        parsedCachedSearches.length - 1
                    );
                }

                // update cache with latest values
                localStorage.setItem(
                    mantleClientCacheKey,
                    JSON.stringify(parsedCachedSearches)
                );
            } else {
                // if we dont have a list already, initialize one & add the searchVal to the start
                const cachedSearchesArr = new Array(5);
                cachedSearchesArr[0] = val;

                localStorage.setItem(
                    mantleClientCacheKey,
                    JSON.stringify(cachedSearchesArr)
                );
            }
        },
        [cachedSearches, parsedCachedSearches]
    );

    // only store debounced searchTerm in cache if
    // there are results for it
    if (data && data?.total_count > 0 && debouncedValue) {
        storeInCache(debouncedValue);
    }

    const getBooleanStrings = (consent: boolean | undefined) => {
        switch (consent) {
            case true:
                return "Yes";
            case false:
                return "No";
            default:
                return "--";
        }
    };

    const columns: ColumnDef<IClient>[] = useMemo(
        () => [
            {
                accessorKey: "client_name",
                header: "CLIENT NAME",
                cell: ({ row }) => (
                    <div className="flex items-center">
                        {displayNameInRightFormat({
                            firstName: row.original.first_name,
                            lastName: row.original.last_name,
                        })}

                        {row.original.age < 18 ? (
                            <div className="flex items-center">
                                <svg
                                    width="2"
                                    height="2"
                                    viewBox="0 0 2 2"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ margin: "0 6px" }}
                                >
                                    <circle
                                        cx="1"
                                        cy="1"
                                        r="1"
                                        fill="#576A81"
                                    />
                                </svg>
                                <Badge content="Minor" type="danger" />
                            </div>
                        ) : null}
                        {row.original.last_login_date &&
                            !Number.isNaN(
                                Date.parse(row.original.last_login_date)
                            ) && (
                                <div className="flex items-center">
                                    <svg
                                        width="2"
                                        height="2"
                                        viewBox="0 0 2 2"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{ margin: "0 6px" }}
                                    >
                                        <circle
                                            cx="1"
                                            cy="1"
                                            r="1"
                                            fill="#576A81"
                                        />
                                    </svg>
                                    <Tooltip
                                        content={
                                            row.original.last_login_date
                                                ? `Last login: ${formatDate(
                                                      row.original
                                                          .last_login_date,
                                                      false
                                                  )}`
                                                : "No login activity"
                                        }
                                        classNames="px-8 py-4"
                                    >
                                        <span>
                                            <Badge
                                                content="Portal"
                                                type="info"
                                            />
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                    </div>
                ),
            },
            {
                accessorKey: "client_chart_number",
                header: "Client Chart number",
                cell: ({ row }) => (
                    <span>{row.original.client_chart_number || "--"}</span>
                ),
            },
            {
                accessorKey: "preferred_name",
                header: "PREFERRED NAME",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {row.original.preferred_name || "--"}
                    </span>
                ),
            },
            {
                accessorKey: "pronouns",
                header: "PRONOUNS",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {row.original.pronouns || "--"}
                    </span>
                ),
            },
            {
                accessorKey: "date_of_birth",
                header: "DATE OF BIRTH",
                cell: ({ row }) => {
                    const dob = row.original.date_of_birth;
                    if (!dob) return "--";

                    const age = getClientAge(dob);
                    return (
                        <div className="flex flex-col">
                            <span>
                                {formatDate(
                                    appendMidnightTimeToDate(dob),
                                    true
                                )}
                            </span>
                            <span className="text-sm text-gray-500">
                                ({age} years)
                            </span>
                        </div>
                    );
                },
            },
            {
                accessorKey: "contact",
                header: "CONTACT",
                cell: ({ row }) => <span>{row.original.contact}</span>,
            },
            {
                accessorKey: "email",
                header: "EMAIL",
                cell: ({ row }) => (
                    <span>
                        {handleDisplayClientEmailCorrectly(row.original.email)}
                    </span>
                ),
            },
            {
                accessorKey: "employer_name",
                header: "employer name",
                cell: ({ row }) => (
                    <span>{row.original.employer_name || "--"}</span>
                ),
            },
            {
                accessorKey: "client_status",
                header: "CLIENT STATUS",
                cell: ({ row }) => {
                    if (row.original.client_status === ClientStatus.INACTIVE) {
                        return (
                            <Badge
                                type="danger"
                                content={row.original.client_status}
                            />
                        );
                    }
                    if (row.original.client_status === ClientStatus.PAUSED) {
                        return (
                            <Badge
                                type="warning"
                                content={row.original.client_status}
                            />
                        );
                    }
                    if (row.original.client_status === ClientStatus.COMPLETED) {
                        return (
                            <Badge
                                type="info"
                                content={row.original.client_status}
                            />
                        );
                    }
                    return (
                        <Badge
                            type="success"
                            content={row.original.client_status}
                        />
                    );
                },
            },
            {
                accessorKey: "tags",
                header: "PROFILE TAGS",
                cell: ({ row }) => {
                    if (!row.original.tags?.length) return "--";
                    return (
                        <span className="flex gap-1 flex-wrap normal-case">
                            {row.original.tags.map((tagKey) => (
                                <Badge
                                    key={tagKey}
                                    type="neutral"
                                    content={CLIENT_TAG_DETAILS[tagKey].label}
                                />
                            ))}
                        </span>
                    );
                },
            },
            {
                accessorKey: "last_session_date",
                header: "Last session date",
                cell: ({ row }) =>
                    row.original.last_session_date
                        ? formatDate(row.original.last_session_date, false)
                        : "--",
            },
            {
                accessorKey: "Client is B2B",
                header: "Client is b2b",
                cell: ({ row }) => (
                    <span>
                        {getBooleanStrings(
                            row.original.client_is_b2b as boolean
                        )}
                    </span>
                ),
            },

            {
                accessorKey: "insurance_provider_name",
                header: "Insurance Provider",
                cell: ({ row }) => (
                    <span
                        className={
                            row.original.self_pay && !showInsOverSelfPay
                                ? "normal-case"
                                : "uppercase"
                        }
                    >
                        {row.original.self_pay && !showInsOverSelfPay
                            ? "Self pay"
                            : (row.original.insurance_provider_name &&
                                  removeEnumUnderscore(
                                      row.original.insurance_provider_name || ""
                                  )) ||
                              "--"}
                    </span>
                ),
            },
            {
                accessorKey: "financial_assistance_status",
                header: "Financial assistance status",
                cell: ({ row }) => (
                    <span className=" capitalize">
                        {row.original.financial_assistance_status || "--"}
                    </span>
                ),
            },
            {
                accessorKey: "consent_to_sms",
                header: "Consent to sms",
                cell: ({ row }) => (
                    <span className=" capitalize">
                        {getBooleanStrings(row.original.consent_to_sms)}
                    </span>
                ),
            },
            {
                accessorKey: "alt_email_sent",
                header: "ALT EMAIL SENT",
                cell: ({ row }) => (
                    <span
                        className={
                            row.original.alt_email_sent
                                ? "text-primary"
                                : "text-danger"
                        }
                    >
                        {row.original.alt_email_sent ? "Yes" : "No"}
                    </span>
                ),
            },
        ],
        [showInsOverSelfPay]
    );

    const table = useReactTable({
        columns,
        data: data?.data || [],
        state: {
            columnVisibility,
        },
        getCoreRowModel: getCoreRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
    });

    const selectedColumnsForExport = table
        .getAllLeafColumns()
        .filter((column) => column.getIsVisible())
        .map(
            (column) =>
                column?.columnDef?.header?.toString()?.toUpperCase() ||
                column.id?.replaceAll("_", " ")?.toUpperCase()
        );

    const exportQuery = useExportClientsList(
        {
            search_string: debouncedValue.toLowerCase(),
            insurance_provider_ids: insuranceProviderIdsFilter,
            provider_id: handleSetProviderId() as string,
            sort_attribute: searchParams.get("sort_attr") || sortConfig.key,
            sort_order: searchParams.get("sort_order") || sortConfig.direction,
            financial_assistance_status: financialAssistanceStatus,
            client_status: clientStatusFilter,
            patient_portal_activation_status: patientPortalActivationStatus,
            selected_columns:
                selectedColumnsForExport && selectedColumnsForExport.length > 0
                    ? selectedColumnsForExport
                    : undefined,
            employer_name: employerName,
            tag_keys: clientTagsFilter,
        },
        isExporting
    );

    const handleExportClick = async () => {
        setIsExporting(true);
        await exportQuery.refetch();
        setIsExporting(false);
        if (exportQuery.error) {
            toast({
                mode: "error",
                message: "Could not export aging reports list",
            });
        }
    };

    return (
        <>
            <Dialog open={Boolean(formInView)}>
                <DialogContent
                    title="Add client"
                    variant="center"
                    submitBtnFormValue={
                        formInView === "add-personal-info"
                            ? addClientPersonalInfoFormValue
                            : "add-session"
                    }
                    showFooter
                    showBackBtn={formInView === "add-session"}
                    saveText={
                        formInView === "add-session"
                            ? "Add Client Session"
                            : "Create and Add Session"
                    }
                    cancelText={
                        formInView === "add-session"
                            ? "Cancel"
                            : "Create Client"
                    }
                    isCancelBtnDisabled={
                        addClient.isLoading || addClientSessionMutating > 0
                    }
                    isSubmitBtnDisabled={
                        addClient.isLoading || addClientSessionMutating > 0
                    }
                    handleCloseDialog={() => handleSetFormInView(null)}
                    onCancelClick={
                        formInView === "add-personal-info"
                            ? handleSubmitAndClose
                            : () => handleSetFormInView(null)
                    }
                    onSaveClick={
                        formInView === "add-personal-info"
                            ? handleSubmitAndContinue
                            : undefined
                    }
                    onBackClick={() => {
                        setProgressStep(FormTypes.ADD_PERSONAL_INFO);
                        handleSetFormInView(FormTypes.ADD_PERSONAL_INFO);
                    }}
                    canCancelButtonSubmit={formInView === "add-personal-info"}
                    cancelBtnFormValue="add-personal-info-save"
                >
                    <div>
                        <Progress
                            steps={Object.values(FormTypes)}
                            currentStep={progressStep}
                            className="top-12"
                        />
                        <div className="pt-5">
                            {formInView === "add-personal-info" && (
                                <>
                                    <p className="text-title text-text mb-[calc(var(--spacing-items)+4px)]">
                                        Personal info
                                    </p>
                                    <AddClient
                                        onFormSubmit={
                                            handleAddClientPersonalDetails
                                        }
                                        submitType={submitType}
                                    />
                                </>
                            )}
                            {formInView === "add-session" && (
                                <>
                                    <span className="text-title text-text">
                                        Session info
                                    </span>
                                    <AddClientSession
                                        onFormSubmit={() =>
                                            handleSetFormInView(null)
                                        }
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <Navbar title={`Clients (${clientsCount})`} />
            <div className="page">
                <Card classNames="mb-[--spacing-header]">
                    <div className="mb-[--spacing-items]">
                        <SearchInput
                            placeholder="Search by name, email, contact, chart number, or insurance id..."
                            onChange={handleSetSearchString}
                            value={search}
                            containerClass="w-[430px] max-w-full"
                        />
                    </div>
                    <div className="flex items-center gap-[--spacing-items] justify-between flex-wrap">
                        <div className="flex items-center gap-[--spacing-items] flex-wrap">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {clientStatusFilter === ""
                                            ? "All"
                                            : makeStringFirstLetterCapital(
                                                  clientStatusFilter
                                              )}
                                    </FilterButton>
                                </DropdownTrigger>

                                <DropdownContent
                                    align="end"
                                    maxHeight={216}
                                    className="overflow-y-auto"
                                    width="auto"
                                >
                                    <DropdownItem
                                        onClick={() =>
                                            handleClientStatusChange("all")
                                        }
                                        isSelected={clientStatusFilter === ""}
                                    >
                                        All
                                    </DropdownItem>

                                    {Object.values(ClientStatus).map(
                                        (status) => (
                                            <DropdownItem
                                                isSelected={
                                                    clientStatusFilter ===
                                                    status
                                                }
                                                key={status}
                                                onClick={() =>
                                                    handleClientStatusChange(
                                                        status
                                                    )
                                                }
                                            >
                                                {status}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>
                            {canViewInsuranceProvidersList && (
                                <InsuranceProviderFilter
                                    values={insuranceProviderIdsFilter}
                                    onChange={handleInsuranceProviderIdsChange}
                                />
                            )}
                            {canViewClientTags && (
                                <ClientTagsFilter
                                    values={clientTagsFilter}
                                    onChange={handleClientTagsChange}
                                />
                            )}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton
                                        disabled={
                                            normalizedEmployerNamesLoading ||
                                            Boolean(
                                                normalizedEmployerNamesError
                                            )
                                        }
                                    >
                                        {(normalizedEmployerNamesLoading &&
                                            "Loading..") ||
                                            (normalizedEmployerNames?.data &&
                                                truncateString(employerName)) ||
                                            "Employer name"}
                                    </FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="start">
                                    <SearchInput
                                        placeholder="Search by employer name"
                                        onChange={(e) =>
                                            setEmployerNameSearchString(
                                                e.target.value
                                            )
                                        }
                                    />

                                    <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                        {normalizedEmployerNamesLoading && (
                                            <Loader />
                                        )}

                                        {normalizedEmployerNamesError && (
                                            <ListState
                                                isError
                                                context="general"
                                                stateHelperText="Please try again later"
                                                errorMsg="Error loading employer names"
                                                cardClass="h-[150px]"
                                            />
                                        )}

                                        {normalizedEmployerNames?.data && (
                                            <>
                                                <Button
                                                    size="auto"
                                                    variant="can-hover"
                                                    className="font-medium capitalize w-full flex items-center justify-between shrink-0 text-[13px]"
                                                    onClick={() => {
                                                        handleEmployerNameChange(
                                                            ""
                                                        );
                                                        setEmployerNameSearchString(
                                                            ""
                                                        );
                                                    }}
                                                >
                                                    All
                                                    {!employerName ? (
                                                        <CheckPrimaryColorIcon />
                                                    ) : null}
                                                </Button>

                                                {(
                                                    normalizedEmployerNames?.data ||
                                                    []
                                                ).map((employer) => (
                                                    <Button
                                                        key={employer}
                                                        size="auto"
                                                        variant="can-hover"
                                                        className="font-medium capitalize w-full flex items-center justify-between shrink-0 text-[13px]"
                                                        onClick={() => {
                                                            handleEmployerNameChange(
                                                                employer
                                                            );
                                                        }}
                                                    >
                                                        {employer}
                                                        {employerName ===
                                                        employer ? (
                                                            <CheckPrimaryColorIcon />
                                                        ) : null}
                                                    </Button>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </PopoverContent>
                            </Popover>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton className="capitalize">
                                        {financialAssistanceStatus ||
                                            "Financial assistance"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent
                                    align={isSmallScreen() ? "start" : "end"}
                                    width={120}
                                >
                                    <DropdownItem
                                        onClick={() =>
                                            handleFinancialAssistanceStatusChange(
                                                ""
                                            )
                                        }
                                        isSelected={!financialAssistanceStatus}
                                    >
                                        All
                                    </DropdownItem>
                                    {Object.values(
                                        FinancialAssistanceStatus
                                    ).map((status) => (
                                        <DropdownItem
                                            onClick={() =>
                                                handleFinancialAssistanceStatusChange(
                                                    status
                                                )
                                            }
                                            key={status}
                                            isSelected={
                                                financialAssistanceStatus ===
                                                status
                                            }
                                        >
                                            {status}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {getPortalStatusDisplayText()}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width={200}>
                                    <DropdownItem
                                        onClick={() =>
                                            handlePatientPortalActivationStatusChange(
                                                ""
                                            )
                                        }
                                        isSelected={
                                            !patientPortalActivationStatus
                                        }
                                    >
                                        All
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() =>
                                            handlePatientPortalActivationStatusChange(
                                                "true"
                                            )
                                        }
                                        isSelected={
                                            patientPortalActivationStatus ===
                                            "true"
                                        }
                                    >
                                        Portal Activated
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() =>
                                            handlePatientPortalActivationStatusChange(
                                                "false"
                                            )
                                        }
                                        isSelected={
                                            patientPortalActivationStatus ===
                                            "false"
                                        }
                                    >
                                        Portal Not Activated
                                    </DropdownItem>
                                </DropdownContent>
                            </Dropdown>
                        </div>
                        <div className="flex items-center gap-[--spacing-items] flex-wrap">
                            {practice?.roles?.includes(UserRole.SUPER_ADMIN) &&
                                canAddClient && (
                                    <Button
                                        aria-label="AddClient"
                                        onClick={() =>
                                            handleSetFormInView(
                                                FormTypes.ADD_PERSONAL_INFO
                                            )
                                        }
                                    >
                                        Add client
                                    </Button>
                                )}
                            <Button
                                onClick={handleExportClick}
                                disabled={
                                    isExporting || data?.data.length === 0
                                }
                            >
                                {isExporting ? "Exporting..." : "Export"}
                            </Button>
                        </div>
                    </div>
                </Card>
                <Card>
                    {isLoading ? <Loader /> : null}
                    {error && (
                        <ListState
                            isError
                            stateHelperText="Try reloading this page to solve this issue"
                            errorMsg={
                                error?.response?.data.message ||
                                `Cannot display clients list at this time please try again later`
                            }
                            context="general"
                        />
                    )}
                    {isSuccess && data && data.data.length === 0 && (
                        <ListState
                            stateHelperText="Client list will appear here once clients are added"
                            isError={false}
                            emptyMessage="No client added yet"
                            wrapperClass={styles.i_stateclass}
                            context="general"
                            emptyBtnProps={
                                practice?.roles?.includes(UserRole.SUPER_ADMIN)
                                    ? {
                                          showButton:
                                              !pageFilter ||
                                              !limitFilter ||
                                              !search,
                                          buttonText: "Add client",
                                          onButtonClick: () =>
                                              handleSetFormInView(
                                                  FormTypes.ADD_PERSONAL_INFO
                                              ),
                                      }
                                    : undefined
                            }
                        />
                    )}
                    {isSuccess && data && data.data.length > 0 && (
                        <>
                            <div>
                                <Table
                                    data={data.data}
                                    columns={columns}
                                    rowSelection={rowSelection}
                                    setRowSelection={setRowSelection}
                                    handleRowClick={handleRowClick}
                                    setSortConfig={setSortConfig}
                                    sortConfig={sortConfig}
                                    sortableColumns={sortableColumns}
                                    hasPagination={data.total_count > 20}
                                    showSizeFilter
                                    isColumnVisible={columnVisibility}
                                    showColumnVisibilityFilter
                                    columnVisibilityDropdownItems={
                                        <>
                                            {table
                                                .getAllLeafColumns()
                                                .filter(
                                                    (column) =>
                                                        column.id !==
                                                            "alt_email_sent" ||
                                                        !showInsOverSelfPay
                                                )
                                                .filter(
                                                    (column) =>
                                                        column.id !== "tags" ||
                                                        canViewClientTags
                                                )
                                                .map((column) => {
                                                    return (
                                                        <DropdownItem
                                                            key={column.id}
                                                            onClick={() =>
                                                                column.toggleVisibility()
                                                            }
                                                            className="*:!uppercase"
                                                            isMulti
                                                            itemValue={
                                                                column.id
                                                            }
                                                            values={table
                                                                .getVisibleLeafColumns()
                                                                .map(
                                                                    (col) =>
                                                                        col.id
                                                                )}
                                                        >
                                                            {column?.columnDef?.header?.toString() ||
                                                                column.id?.replaceAll(
                                                                    "_",
                                                                    " "
                                                                )}
                                                        </DropdownItem>
                                                    );
                                                })}
                                        </>
                                    }
                                    pagination={
                                        <Pagination
                                            totalCount={data.total_count}
                                            totalPages={data.total_pages}
                                            currentPage={pageFilter}
                                            onPageChange={handlePageChange}
                                            limit={limitFilter}
                                            onLimitChange={handleLimitChange}
                                        />
                                    }
                                />
                            </div>
                            {/* <div className={styles.list_mobile} role="table">
                                {data.data.map((client) => (
                                    <MobileListItem
                                        key={client.client_id}
                                        className={styles.list_card}
                                        showCheckButton={false}
                                        onClickListItem={() =>
                                            handleMobileItemClick(
                                                client.client_id
                                            )
                                        }
                                    >
                                        <span className={styles.client}>
                                            <span
                                                className={styles.client_wrap}
                                            >
                                                <span className={styles.name}>
                                                    {displayNameInRightFormat({
                                                        firstName:
                                                            client.first_name,
                                                        lastName:
                                                            client.last_name,
                                                    })}
                                                </span>
                                                {isMinor(client.age) && (
                                                    <>
                                                        <span
                                                            className={cn(
                                                                styles.dot,
                                                                styles.dot_small
                                                            )}
                                                        />
                                                        <Badge
                                                            content="Minor"
                                                            textColor="#981F41"
                                                            bgColor="rgba(251, 199, 198, 0.50)"
                                                        />
                                                    </>
                                                )}
                                                {client.last_login_date &&
                                                    !Number.isNaN(
                                                        Date.parse(
                                                            client.last_login_date
                                                        )
                                                    ) && (
                                                        <>
                                                            <span
                                                                className={cn(
                                                                    styles.dot,
                                                                    styles.dot_small
                                                                )}
                                                            />
                                                            <Tooltip
                                                                content={
                                                                    client.last_login_date
                                                                        ? `Last login: ${formatDate(
                                                                              client.last_login_date,
                                                                              false
                                                                          )}`
                                                                        : "No login activity"
                                                                }
                                                                classNames="px-8 py-4"
                                                            >
                                                                <span>
                                                                    <Badge
                                                                        content="Portal"
                                                                        textColor="#00563E"
                                                                        bgColor="rgba(204, 250, 233, 0.50)"
                                                                    />
                                                                </span>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                            </span>
                                            <span
                                                className={styles.client_wrap}
                                            >
                                                <span
                                                    className={cn(
                                                        styles.tlight,
                                                        "capitalize"
                                                    )}
                                                >
                                                    {client.preferred_name ||
                                                        "--"}
                                                </span>
                                                <span
                                                    className={cn(
                                                        styles.dot,
                                                        styles.dot_large
                                                    )}
                                                />
                                                <span
                                                    className={cn(
                                                        styles.tlight,
                                                        "capitalize"
                                                    )}
                                                >
                                                    {client.pronouns || "--"}
                                                </span>
                                                <span
                                                    className={cn(
                                                        styles.dot,
                                                        styles.dot_large
                                                    )}
                                                />
                                                <span className={styles.tlight}>
                                                    {client.age || "--"}
                                                </span>
                                            </span>
                                            <span
                                                className={styles.client_wrap}
                                            >
                                                <span className={styles.tlight}>
                                                    {client.contact || "--"}
                                                </span>
                                                <span
                                                    className={cn(
                                                        styles.dot,
                                                        styles.dot_large
                                                    )}
                                                />
                                                <span className={styles.tlight}>
                                                    {client.email || "--"}
                                                </span>
                                            </span>
                                            <span
                                                className={styles.client_wrap}
                                            >
                                                <span
                                                    className={cn(
                                                        styles.tlight,
                                                        client.self_pay
                                                            ? "!normal-case"
                                                            : "!uppercase"
                                                    )}
                                                >
                                                    <span className="normal-case text-gray">
                                                        Insurance Provider
                                                    </span>
                                                    {`: ${
                                                        client.self_pay &&
                                                        !showInsOverSelfPay
                                                            ? "Self pay"
                                                            : (client.insurance_provider_name &&
                                                                  removeEnumUnderscore(
                                                                      client.insurance_provider_name ||
                                                                          ""
                                                                  )) ||
                                                              "--"
                                                    }`}
                                                </span>
                                            </span>
                                            <span
                                                className={styles.client_wrap}
                                            >
                                                <span className={styles.tlight}>
                                                    <span className="normal-case text-gray">
                                                        Financial assistance
                                                        status
                                                    </span>
                                                    {": "}
                                                    <span className="capitalize">
                                                        {client.financial_assistance_status ||
                                                            "--"}
                                                    </span>
                                                </span>
                                            </span>
                                            <span
                                                className={styles.client_wrap}
                                            >
                                                <span className={styles.tlight}>
                                                    <span className="normal-case text-gray">
                                                        Employer name
                                                    </span>
                                                    {": "}
                                                    <span className="capitalize">
                                                        {client.employer_name ||
                                                            "--"}
                                                    </span>
                                                </span>
                                            </span>
                                        </span>
                                    </MobileListItem>
                                ))}
                                {data.total_count > 20 && (
                                    <div className="bg-white rounded-r8 p-12 mt-16">
                                        <Pagination
                                            totalCount={data.total_count}
                                            totalPages={data.total_pages}
                                            currentPage={pageFilter}
                                            onPageChange={handlePageChange}
                                            limit={limitFilter}
                                            onLimitChange={handleLimitChange}
                                        />
                                    </div>
                                )}
                            </div> */}
                        </>
                    )}
                </Card>
            </div>
        </>
    );
}
